import { API_MODE, getS3Object } from '@meteor/frontend-core';
import { Card, Space, Flex, Select, Form, DatePicker, Modal, Table, Input, Button, Tooltip, Splitter } from 'antd';
import { EVALUATION_PERIOD } from '../../enum/common.enum';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import APIList from '../../http/ApiList';
import { ToshibaContext } from '../../contexts/toshiba.context';
import { convertToJSON } from '../../utils/commonUtil';
import { AppContext } from '../../contexts/AppContext';
import { divide, groupBy, sortBy, sumBy, union } from 'lodash';
import { KPI_CODE } from '../../enum/toshiba.enum';
import { useTralitSetting, NAME_PATH } from '../../hooks/useTralitSetting';
import ContentChart from '../base/content-chart';
import { ControlOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { LabelTag } from '../common/label-tag';
import { useSelectorSize } from '../../hooks/useWindowSize';
import dayjs from 'dayjs';
import { ColumnType } from 'antd/es/table';
import { buSortMap, segmentSortSeqMap } from '../../contexts/provider/toshiba.provider';
import { useSnapshot } from '../../hooks/useSnapshot';
import DiffHeader from './diff-header';
import { useSessionStorageState } from 'ahooks';

type DiffPageProps = {};

const DiffPage: React.FC<DiffPageProps> = (props) => {
  const { color } = useContext(AppContext);
  const { kpiValue, kpiUnitLabel, drillFields, setDrillFields, filter, setFilter, getAggregationPeriod } = useContext(ToshibaContext);
  const { fontSize } = useTralitSetting({ namePath: NAME_PATH.PREFERENCE });
  const [dataProvider, setDataProvider] = useState<any>({ data: [], target: [], plan: [], prefYear: [], compare: [] });
  const [loading, setLoading] = useState(true);

  const { currentSnapshot, prefSnapshot, getMonthLabel } = useSnapshot();

  const searchParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(searchParams);

  const defaultDrillDownRule = useMemo(() => ['segment', 'businessUnit', 'orderStatus', 'salesBasis'], []);
  const [drillDownRule, setDrillDownRule] = useSessionStorageState<any>('diffDrillDown', {
    defaultValue: defaultDrillDownRule,
  });

  // 获取Table滚动条高度
  const { selectorHeight4Table } = useSelectorSize({
    selector: '.tralit-table-tbody',
    watcherSelector: ['.tralit-splitter-panel', '.tralit-card', '.tralit-table-header', 'tralit-card-head-title', '.summary-kpi-card'],
  });

  const curDrillFieldKey = useMemo(() => {
    const lastDrillField = drillFields.at(-1);
    if (lastDrillField) {
      if (drillDownRule.length === drillDownRule.indexOf(lastDrillField.fieldKey)) {
        return drillDownRule.at(-1);
      }
      return drillDownRule[drillDownRule.indexOf(lastDrillField.fieldKey) + 1];
    } else {
      return drillDownRule[0];
    }
  }, [drillDownRule, drillFields]);

  const filterMethod = useCallback(
    () => (item) => {
      return drillFields.every((drillField) => drillField.value.includes(item[drillField.fieldKey]));
    },
    [drillFields, currentSnapshot, filter]
  );

  // form
  const [form] = Form.useForm();

  const getData = ({ targetSnapshot, fiscalYear, evaluationPeriod }) => {
    if (!currentSnapshot || !targetSnapshot) {
      return;
    }
    setLoading(true);
    // 计算集計期間
    const aggregationPeriod = getAggregationPeriod(currentSnapshot, 'FY25', evaluationPeriod);
    if (aggregationPeriod) {
      const [startDate, endDate] = aggregationPeriod;
      APIList.getDiffData()
        .post({
          apiMode: API_MODE.LONG_RESPONSE,
          currentSnapshot,
          targetSnapshot,
          fiscalYear,
          aggregationPeriod: [startDate.format('YYYYMM'), endDate.format('YYYYMM')],
        })
        .then(async (data) => {
          const response = await getS3Object(process.env.REACT_APP_BUCKET_NAME_TOSHIBA, data.bucketKey);
          setDataProvider({
            data: convertToJSON(response.data),
            target: convertToJSON(response.target),
            compare: convertToJSON(response.compare),
            plan: convertToJSON(response.plan),
          });

          const filter = {
            targetSnapshot,
            fiscalYear,
            evaluationPeriod,
            aggregationPeriod,
          };
          setFilter(filter);
          form.setFieldsValue(filter);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (currentSnapshot) {
      getData({
        fiscalYear: 'FY25',
        evaluationPeriod: params.evaluationPeriod || filter.evaluationPeriod || EVALUATION_PERIOD.FULL_PERIOD,
        targetSnapshot: filter.targetSnapshot || prefSnapshot,
      });
      if (drillFields.findIndex((item) => item.fieldKey === 'model') > 1) {
        const [segment, businessUnit] = drillFields;
        setDrillFields([segment, businessUnit]);
      }
    }
  }, [currentSnapshot, prefSnapshot]);

  const toolTipFormatter = (params) => {
    return `${params
      .map((param) => {
        if (['placeholder', '总数'].includes(param.seriesName) || !param.value) {
          return '';
        }
        return `<div><span>${param.marker}</span> <span>${param.seriesName}: </span><span>${kpiValue(
          KPI_CODE.SALES_AMOUNT,
          param.value,
          false,
          {
            [KPI_CODE.SALES_AMOUNT]: {
              unitValue: 100000000,
              unitLabel: '億円',
              precision: 2,
            },
          }
        )}</span></div>`;
      })
      .join('')}`;
  };

  const chartOptions = useMemo(() => {
    const { data: orgData, target: orgTarget, plan: orgPlan } = dataProvider;

    const data = orgData.filter(filterMethod());
    const target = orgTarget.filter(filterMethod());
    const plan = orgPlan.filter(filterMethod());

    const gbCategoryCur = groupBy(data, curDrillFieldKey);
    const gbCategoryTar = groupBy(target, curDrillFieldKey);
    const gbCategoryPlan = groupBy(plan, curDrillFieldKey);
    let drillFieldValues = union([...Object.keys(gbCategoryCur), ...Object.keys(gbCategoryTar), ...Object.keys(gbCategoryPlan)]);

    console.log(gbCategoryCur);

    const targetTotal = sumBy(target, KPI_CODE.SALES_AMOUNT);
    const dataTotal = sumBy(data, KPI_CODE.SALES_AMOUNT);
    const planTotal = sumBy(plan, KPI_CODE.SALES_AMOUNT);
    switch (curDrillFieldKey) {
      case 'segment':
        drillFieldValues.sort((a, b) => segmentSortSeqMap[a] - segmentSortSeqMap[b]);
        break;
      case 'businessUnit':
        const map = buSortMap[drillFields[0].value];
        if (drillFields[0].value[0] === '原子力') {
          drillFieldValues = drillFieldValues.filter((dv) => dv !== '国内');
        }
        drillFieldValues.sort((a, b) => map[a] - map[b]);
        break;
      default:
        break;
    }

    const compareData = drillFieldValues.reduce(
      (org, fieldValue) => {
        const targetCompare = target.filter((item) => item[curDrillFieldKey] === fieldValue);
        const dataCompare = data.filter((item) => item[curDrillFieldKey] === fieldValue);
        const targetCompareTotal = sumBy(targetCompare, KPI_CODE.SALES_AMOUNT);
        const dataCompareTotal = sumBy(dataCompare, KPI_CODE.SALES_AMOUNT);
        if (dataCompareTotal > targetCompareTotal) {
          org.increase.push(dataCompareTotal - targetCompareTotal);
          org.decrease.push(0);
          org.placeholder.push(org.lastValue);
          org.lastValue = org.lastValue + (dataCompareTotal - targetCompareTotal);
          org.fieldValues.push(fieldValue);
        } else if (dataCompareTotal < targetCompareTotal) {
          org.increase.push(0);
          org.decrease.push(targetCompareTotal - dataCompareTotal);
          org.placeholder.push(org.lastValue - (targetCompareTotal - dataCompareTotal));
          org.lastValue = org.lastValue - (targetCompareTotal - dataCompareTotal);
          org.fieldValues.push(fieldValue);
        }
        return org;
      },
      {
        fieldValues: [],
        lastValue: targetTotal,
        placeholder: [],
        increase: [],
        decrease: [],
      }
    );

    const getFieldValues = () => {
      if (curDrillFieldKey === 'orderStatus') {
        const order = ['既受注', '未受注', '白地', '調整'];
        return sortBy(compareData.fieldValues, (value) => {
          return order.indexOf(value);
        });
      }
      return compareData.fieldValues;
    };

    const xAxisData = [
      `比較断面(${getMonthLabel(filter.targetSnapshot)})`,
      ...getFieldValues(),
      `今月断面(${getMonthLabel(currentSnapshot)})`,
    ];

    return {
      title: {
        value: '',
        styles: {
          fontSize: fontSize.chartTitle,
          margin: 0,
        },
      },
      tooltips: ['zoomIn', 'reset'],
      chartOptions: {
        textStyle: {
          fontFamily:
            "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif,'Apple Color Emoji','Segoe UI Emoji','Segoe UI Symbol','Noto Color Emoji'",
        },
        animation: false,
        dataZoom: [
          {
            type: 'slider',
            yAxisIndex: 0,
            filterMode: 'none',
            showDetail: false,
          },
          {
            type: 'inside',
            yAxisIndex: 0,
            filterMode: 'none',
          },
        ],
        xAxis: [
          {
            type: 'category',
            nameGap: 10,
            axisLabel: {
              interval: 0,
              hideOverlap: true,
              fontSize: fontSize.chart,
              // formatter: (value) => dayjs(value).format(dimension.format),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },

            data: xAxisData,
          },
          {
            show: false,
            type: 'category',
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            data: xAxisData,
          },
        ],
        yAxis: [
          {
            type: 'value',
            name: `値(億円)`,
            nameGap: 10,
            axisLabel: {
              fontSize: fontSize.chart,
              formatter: (value) => kpiValue(KPI_CODE.SALES_AMOUNT, value),
            },
            nameTextStyle: {
              fontSize: fontSize.chart,
            },
            scale: true,
            // min: minY,
          },
        ],
        grid: {
          top: 45,
          right: 60,
          bottom: 40,
          left: 85,
        },
        legend: {
          data:
            drillFields.length < 3
              ? ['予算', ...compareData.fieldValues, '超過分', '未達分']
              : [...compareData.fieldValues, '超過分', '未達分'],
          selectedMode: false,
          textStyle: {
            fontSize: fontSize.chart,
          },
        },
        tooltip: {
          trigger: 'axis',
          appendToBody: true,
          axisPointer: {
            lineStyle: {
              width: 2,
            },
          },
          formatter: toolTipFormatter,
        },
        series: [
          {
            id: 'placeholder',
            name: 'placeholder',
            type: 'bar',
            stack: 'drill',
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent',
            },
            emphasis: {
              itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
              },
            },
            xAxisIndex: 0,
            data: [0, ...compareData.placeholder, 0],
            label: {
              show: false,
            },
            barMaxWidth: 50,
          },
          ...drillFieldValues.map((drillFieldValue) => {
            const tarData = gbCategoryTar[drillFieldValue];
            const curData = gbCategoryCur[drillFieldValue];
            const tarValue = sumBy(tarData, KPI_CODE.SALES_AMOUNT);
            const curValue = sumBy(curData, KPI_CODE.SALES_AMOUNT);
            return {
              name: drillFieldValue,
              type: 'bar',
              stack: 'drill',
              data: [tarValue, ...compareData.fieldValues.map(() => 0), curValue],
              label: {
                show: false,
              },
              barMaxWidth: 50,
            };
          }),
          {
            id: 'decrease',
            name: '未達分',
            type: 'bar',
            stack: 'drill',
            itemStyle: {
              color: color.errorColor,
            },
            xAxisIndex: 0,
            data: [0, ...compareData.decrease, 0],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) =>
                params.dataIndex !== 0 && params.dataIndex !== xAxisData.length - 1 && params.value
                  ? `-${kpiValue(KPI_CODE.SALES_AMOUNT, params.value)}`
                  : '',
              color: color.errorColor,
            },
            barMaxWidth: 50,
          },
          {
            id: 'increase',
            name: '超過分',
            type: 'bar',
            stack: 'drill',
            itemStyle: {
              color: color.successColor,
            },
            xAxisIndex: 0,
            data: [0, ...compareData.increase, 0],
            label: {
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) =>
                params.dataIndex !== 0 && params.dataIndex !== xAxisData.length - 1 && params.value
                  ? `+${kpiValue(KPI_CODE.SALES_AMOUNT, params.value)}`
                  : '',
              color: color.successColor,
            },
            barMaxWidth: 50,
          },
          {
            name: '总数',
            type: 'bar',
            stack: 'drill',
            data: [{ value: 1, show: targetTotal }, ...compareData.fieldValues.map(() => 0), { value: 1, show: dataTotal }],
            label: {
              show: true,
              position: 'top',
              fontSize: fontSize.chart,
              formatter: (params) => {
                return params.data.show ? kpiValue(KPI_CODE.SALES_AMOUNT, params.data.show) : '';
              },
            },
            itemStyle: {
              color: 'transparent', // 使其不可见
            },
            emphasis: {
              itemStyle: {
                color: 'transparent', // 鼠标悬停时也透明
              },
            },
            silent: true, // 禁用鼠标交互
          },
          drillFields.length < 3 && {
            name: '予算',
            type: 'line',
            symbol: 'none',
            data: [planTotal, ...compareData.fieldValues.map(() => planTotal), planTotal],
            silent: true,
            itemStyle: {
              color: '#ff8331',
              borderType: 'dotted',
            },
            lineStyle: {
              type: 'dashed',
            },
            label: {
              show: false,
            },
            z: 0,
          },
        ],
      },
      height: document.body.clientHeight / 3,
    };
  }, [dataProvider, filter, curDrillFieldKey]);

  const seriesClickHandler = (e) => {
    if (drillDownRule.at(-1) !== curDrillFieldKey && (e.seriesId === 'increase' || e.seriesId === 'decrease')) {
      setDrillFields((pref) => [...pref, { fieldKey: curDrillFieldKey, value: [e.name] }]);
    }
  };

  const ChartComponent = useCallback(() => {
    return (
      <Card styles={{ body: { minHeight: document.body.clientHeight / 3 + 20, padding: 0 } }}>
        <ContentChart
          contentList={[]}
          {...chartOptions}
          showSeriesLabel
          mouseWheelZoomLock={false}
          loading={loading}
          tooltips={['zoomIn']}
          isBank={!dataProvider || !dataProvider.data || dataProvider.data.length === 0}
          onClick={seriesClickHandler}
        />
      </Card>
    );
  }, [chartOptions, loading, dataProvider]);

  const searchInput = useRef(null);

  const getColumnSearchProps = useCallback((dataIndex: string, title: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, close }) => (
        <div style={{ padding: 8, width: 320 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            style={{ width: '100%' }}
            allowClear
            ref={searchInput}
            placeholder={`${title}`}
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => {
              confirm();
            }}
          />
          <Flex style={{ marginTop: 6 }} justify="flex-end">
            <Button
              type="primary"
              size="small"
              onClick={() => {
                confirm();
              }}
            >
              Search
            </Button>
          </Flex>
        </div>
      ),
      filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? color.primaryColor : undefined }} />,
      filterDropdownProps: {
        onOpenChange(open) {
          if (open) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
      },
      onFilter: (value, record) => {
        return record[dataIndex].includes(value);
      },
    };
  }, []);
  const segFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.segment) ? pr : pr.concat(pt.segment)), []),
    [dataProvider]
  );
  const buFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.businessUnit) ? pr : pr.concat(pt.businessUnit)), []),
    [dataProvider]
  );
  const salesDepFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.model) ? pr : pr.concat(pt.model)), []),
    [dataProvider]
  );
  const salesSecFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.location) ? pr : pr.concat(pt.location)), []),
    [dataProvider]
  );
  const departmentFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.salesDepartment) ? pr : pr.concat(pt.salesDepartment)), []),
    [dataProvider]
  );
  const orderStatusFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.orderStatus) ? pr : pr.concat(pt.orderStatus)), []),
    [dataProvider]
  );
  const salesBasisFilters = useMemo(
    () => dataProvider.compare.reduce((pr, pt) => (pr.includes(pt.salesBasis) ? pr : pr.concat(pt.salesBasis)), []),
    [dataProvider]
  );

  const columns: ColumnType[] = useMemo(() => {
    const render = (val) => (
      // <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={val}>
      //   {val}
      // </div>
      <Tooltip title={<div style={{ fontSize: fontSize.table }}>{val}</div>}>
        <div style={{ textAlign: 'left', overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val}</div>
      </Tooltip>
    );
    return [
      {
        title: 'セグメント',
        dataIndex: 'segment',
        fixed: 'left',
        width: 140,
        filters: segFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.segment.includes(value);
          return record.segment === value;
        },
      },
      {
        title: 'BU',
        dataIndex: 'businessUnit',
        fixed: 'left',
        width: 120,
        filters: buFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.businessUnit.includes(value);
          return record.businessUnit === value;
        },
        render,
      },
      {
        title: '案件名',
        dataIndex: 'projectName',
        fixed: 'left',
        width: 180,
        render,
        ...getColumnSearchProps('projectName', '案件名'),
      },
      {
        title: '売上基準',
        width: 120,
        dataIndex: 'salesBasis',
        filters: salesBasisFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.salesBasis.includes(value);
          return record.salesBasis === value;
        },
        render,
      },
      {
        title: '機種',
        width: 180,
        dataIndex: 'model',
        filters: salesDepFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          // return record.model.includes(value);
          return record.model === value;
        },
        render,
      },
      // {
      //   title: '顧客',
      //   dataIndex: 'customer',
      //   width: 280,
      //   render,
      //   ...getColumnSearchProps('customer', '顧客'),
      // },
      {
        title: '設置場所名',
        width: 180,
        dataIndex: 'location',
        // filters: salesSecFilters.map((s) => ({ text: s, value: s })),
        // onFilter: (value, record) => {
        //   return record.location.includes(value);
        // },
        render,
        ...getColumnSearchProps('customer', '設置場所'),
      },
      {
        title: '営業部',
        dataIndex: 'salesDepartment',
        width: 120,
        filters: departmentFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          return record.salesDepartment === value;
        },
        render,
      },
      {
        title: '受注区分',
        width: 140,
        dataIndex: 'orderStatus',
        filters: orderStatusFilters.map((s) => ({ text: s, value: s })),
        onFilter: (value, record) => {
          return record.orderStatus.includes(value);
        },
        render,
        sorter: {
          compare: (a, b) => {
            const order = ['未受注', '白地', '既受注', '調整'];
            return order.indexOf(a.orderStatus) - order.indexOf(b.orderStatus);
          },
          multiple: 1,
        },
      },
      // {
      //   title: `売上${kpiUnitLabel(KPI_CODE.SALES_AMOUNT, {
      //     [KPI_CODE.SALES_AMOUNT]: {
      //       unitValue: 1000000,
      //       unitLabel: '百万',
      //     },
      //   })}`,
      //   dataIndex: 'salesAmount',
      //   width: 120,
      //   defaultSortOrder: 'descend',
      //   sorter: {
      //     compare: (a, b) => a.salesAmount - b.salesAmount,
      //     multiple: 2,
      //   },
      //   render: (val) => {
      //     return (
      //       <div style={{ textAlign: 'right' }}>
      //         {kpiValue(KPI_CODE.SALES_AMOUNT, val, false, {
      //           [KPI_CODE.SALES_AMOUNT]: {
      //             unitValue: 1000000,
      //             unitLabel: '百万',
      //           },
      //         })}
      //       </div>
      //     );
      //   },
      // },
      {
        title: <div>今回</div>,
        dataIndex: 'cur',
        width: 100,
        sorter: (a, b) => a.cur - b.cur,
        render: (val) => {
          return (
            <div style={{ textAlign: 'right' }}>
              {kpiValue(KPI_CODE.SALES_AMOUNT, val, false, {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 1000000,
                  unitLabel: '百万',
                  precision: 0,
                },
              })}
            </div>
          );
        },
      },
      {
        title: <div>前回</div>,
        dataIndex: 'prev',
        width: 100,
        sorter: (a, b) => a.prev - b.prev,
        render: (val) => {
          return (
            <div style={{ textAlign: 'right' }}>
              {kpiValue(KPI_CODE.SALES_AMOUNT, val, false, {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 1000000,
                  unitLabel: '百万',
                  precision: 0,
                },
              })}
            </div>
          );
        },
      },
      {
        title: <div>変動額</div>,
        dataIndex: 'diff',
        width: 100,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => a.diff - b.diff,
        render: (val) => {
          return (
            <div style={{ textAlign: 'right' }}>
              {kpiValue(KPI_CODE.SALES_AMOUNT, val, true, {
                [KPI_CODE.SALES_AMOUNT]: {
                  unitValue: 1000000,
                  unitLabel: '百万',
                  precision: 0,
                },
              })}
            </div>
          );
        },
      },
      {
        title: '変動割合',
        dataIndex: 'diffRate',
        width: 120,
        sorter: (a, b) => a.diffRate - b.diffRate,
        render: (val) => {
          return <div style={{ textAlign: 'right' }}>{kpiValue(KPI_CODE.DIFF_RATE, val, true)}</div>;
        },
      },
    ];
  }, [segFilters, buFilters, salesDepFilters, salesSecFilters, orderStatusFilters, salesBasisFilters, departmentFilters]);

  return (
    <>
      <DiffHeader
        submit={getData}
        form={form}
        loading={loading}
        drillDownRule={drillDownRule}
        setDrillDownRule={setDrillDownRule}
        defaultDrillDownRule={defaultDrillDownRule}
      />
      <Splitter layout="vertical" style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }}>
        <Splitter.Panel
          resizable={false}
          collapsible={{ start: true, end: true, showCollapsibleIcon: true }}
          defaultSize={document.body.clientHeight / 3 + 22}
        >
          <ChartComponent />
        </Splitter.Panel>
        <Splitter.Panel resizable={false}>
          <Card
            title={'未受注案件明細'}
            styles={{
              body: { padding: 0 },
              header: { color: '#555', fontWeight: 'bold', fontSize: fontSize.tableTitle, padding: '0 12px' },
            }}
            classNames={{ header: 'list-card-title' }}
            extra={
              <span style={{ fontSize: fontSize.tag }}>
                単位：
                {kpiUnitLabel(
                  KPI_CODE.SALES_AMOUNT,
                  {
                    [KPI_CODE.SALES_AMOUNT]: {
                      unitValue: 1000000,
                      unitLabel: '百万円',
                    },
                  },
                  false
                )}
              </span>
            }
          >
            <Table
              virtual
              size="small"
              className="small-header"
              bordered
              rowHoverable={false}
              rowKey={'category'}
              columns={columns as any[]}
              dataSource={dataProvider.compare.filter(filterMethod()).sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))}
              pagination={false}
              scroll={{
                // x: 'max-content',
                y: selectorHeight4Table,
              }}
              loading={loading}
              rowClassName={(record: any, index) => {
                if (record.projectName === '調整') {
                  return 'row-grey';
                }
                return '';
              }}
            />
          </Card>
        </Splitter.Panel>
      </Splitter>
    </>
  );
};

export default DiffPage;
